// Custom HTML / CSS editor for print-template builders that opt into
// `mode: 'custom_html'` (Hybrid Builder Extension — see
// PRINT_TEMPLATE_PLAN.md "§Hybrid Builder Extension").
//
// Two side-by-side textareas: HTML on the left, CSS on the right.
// A shared variable palette below lets the user click a token chip to
// insert `{{token}}` at the cursor position of whichever textarea was
// last focused. Stacks vertically below 980 px viewport.

import { useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';

import { Button } from '../../../../components/ui/Button/Button';
import { getVariablesForType } from '../variables';
import type { PrintTemplateType } from '../../../../types/api';
import styles from './CustomHtmlEditor.module.css';

type FocusTarget = 'html' | 'css';

export interface CustomHtmlEditorProps {
  /** Active HTML template body. */
  htmlValue: string;
  /** Active CSS rules. */
  cssValue: string;
  /** Callbacks fire on every keystroke; the parent owns the state. */
  onHtmlChange: (next: string) => void;
  onCssChange: (next: string) => void;
  /** Used to pick the right palette variable list. */
  templateType: PrintTemplateType;
}

export function CustomHtmlEditor({
  htmlValue,
  cssValue,
  onHtmlChange,
  onCssChange,
  templateType,
}: CustomHtmlEditorProps): JSX.Element {
  const htmlRef = useRef<HTMLTextAreaElement | null>(null);
  const cssRef = useRef<HTMLTextAreaElement | null>(null);
  const [lastFocus, setLastFocus] = useState<FocusTarget>('html');
  const [paletteOpen, setPaletteOpen] = useState(false);

  const variables = getVariablesForType(templateType);

  function insertToken(token: string) {
    const tag = `{{${token}}}`;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(tag).catch(() => undefined);
    }
    const target = lastFocus === 'css' ? cssRef.current : htmlRef.current;
    const value = lastFocus === 'css' ? cssValue : htmlValue;
    const onChange = lastFocus === 'css' ? onCssChange : onHtmlChange;
    if (target === null) {
      onChange(`${value}${tag}`);
      return;
    }
    const start = target.selectionStart ?? value.length;
    const end = target.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const after = value.slice(end);
    onChange(`${before}${tag}${after}`);
    requestAnimationFrame(() => {
      const fresh = lastFocus === 'css' ? cssRef.current : htmlRef.current;
      if (fresh === null) return;
      fresh.focus();
      const cursor = before.length + tag.length;
      fresh.setSelectionRange(cursor, cursor);
    });
  }

  function handleDragStart(token: string) {
    return (e: React.DragEvent<HTMLButtonElement>) => {
      e.dataTransfer.setData('text/plain', `{{${token}}}`);
      e.dataTransfer.effectAllowed = 'copy';
    };
  }

  function handleDrop(target: FocusTarget) {
    return (e: React.DragEvent<HTMLTextAreaElement>) => {
      const data = e.dataTransfer.getData('text/plain');
      if (data.length === 0) return;
      e.preventDefault();
      const ta = target === 'css' ? cssRef.current : htmlRef.current;
      const value = target === 'css' ? cssValue : htmlValue;
      const onChange = target === 'css' ? onCssChange : onHtmlChange;
      const start = ta?.selectionStart ?? value.length;
      const end = ta?.selectionEnd ?? value.length;
      const before = value.slice(0, start);
      const after = value.slice(end);
      onChange(`${before}${data}${after}`);
    };
  }

  return (
    <section
      className={styles.shell}
      role="region"
      aria-label="Custom HTML editor"
    >
      <div className={styles.pane}>
        <div className={styles.headerRow}>
          <label className={styles.label} htmlFor="pt-custom-html">
            HTML Template
          </label>
          <Button
            variant="ghost"
            size="sm"
            icon={Sparkles}
            onClick={() => setPaletteOpen((o) => !o)}
            aria-expanded={paletteOpen}
            aria-label="Show variables"
          >
            Variables
          </Button>
        </div>
        <p className={styles.hint}>
          Paste or type your HTML here. Use {'{{variable}}'} tokens
          anywhere — see the variable palette.
        </p>
        <textarea
          id="pt-custom-html"
          ref={htmlRef}
          className={styles.editor}
          value={htmlValue}
          onChange={(e) => onHtmlChange(e.target.value)}
          onFocus={() => setLastFocus('html')}
          onDrop={handleDrop('html')}
          onDragOver={(e) => e.preventDefault()}
          placeholder='<article class="receipt">...</article>'
          spellCheck={false}
          aria-label="HTML template"
        />
      </div>
      <div className={styles.pane}>
        <label className={styles.label} htmlFor="pt-custom-css">
          CSS Styles
        </label>
        <p className={styles.hint}>
          Plain CSS. Reference brand colours via{' '}
          <code>var(--color-primary)</code> etc.
        </p>
        <textarea
          id="pt-custom-css"
          ref={cssRef}
          className={styles.editor}
          value={cssValue}
          onChange={(e) => onCssChange(e.target.value)}
          onFocus={() => setLastFocus('css')}
          onDrop={handleDrop('css')}
          onDragOver={(e) => e.preventDefault()}
          placeholder="/* Your custom styles */"
          spellCheck={false}
          aria-label="CSS styles"
        />
      </div>
      {paletteOpen ? (
        <div
          className={styles.paletteWrap}
          role="region"
          aria-label="Custom HTML variable palette"
          style={{ gridColumn: '1 / -1' }}
        >
          <h4 className={styles.label} style={{ marginBottom: '0.5rem' }}>
            Available variables
          </h4>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.4rem',
            }}
          >
            {variables.map((v) => (
              <li key={v.token}>
                <button
                  type="button"
                  draggable
                  onClick={() => insertToken(v.token)}
                  onDragStart={handleDragStart(v.token)}
                  title={v.description}
                  style={{
                    fontFamily: 'ui-monospace, monospace',
                    fontSize: '0.78rem',
                    padding: '0.25rem 0.5rem',
                    border: '1px solid var(--color-card-border)',
                    background: 'var(--color-card-bg)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {`{{${v.token}}}`}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
